/**
 * @module main
 * @description Main entry point for @ufe/components package.
 * This module provides shared UI components for the UFE monorepo.
 * All components are designed to be safe for server-side rendering (SSR).
 *
 * Components follow Atomic Design principles:
 * - Atoms: Basic building blocks (buttons, inputs, labels)
 * - Molecules: Simple combinations of atoms (form fields, cards)
 * - Organisms: Complex UI components (headers, forms, product cards)
 * - Templates: Page-level layouts
 *
 * @example
 * // Import everything
 * import { description, atoms } from '@ufe/components';
 *
 * @example
 * // Import specific components (when available)
 * import { Button } from '@ufe/components/atoms/button';
 */

import { Button, ButtonVariant, ButtonType } from 'atoms/button';

/**
 * @constant {Object} BuildTargetEnvironment - Build target environment identifiers
 * @property {string} Browser - Browser environment identifier
 * @property {string} Node - Node.js environment identifier
 */
const BuildTargetEnvironment = {
    Browser: 'Browser',
    Node: 'Node'
} as const;

type BuildTargetEnvironmentType = (typeof BuildTargetEnvironment)[keyof typeof BuildTargetEnvironment];

let targetEnvironment: BuildTargetEnvironmentType = BuildTargetEnvironment.Browser;

if (__NODE_BUILD__) {
    targetEnvironment = BuildTargetEnvironment.Node;
}

/**
 * @constant {string} description - Package description including build environment
 */
const description = `Shared UI components for @ufe monorepo. ${targetEnvironment} environment build.`;

/**
 * @constant {Object} atoms - Atomic Design atoms (basic building blocks)
 * @property {React.FC} Button - Button component with variants
 * @property {Object} ButtonVariant - Button variant constants
 * @property {Object} ButtonType - Button type constants
 */
const atoms = {
    Button,
    ButtonVariant,
    ButtonType
};

export { description, atoms };
